import { useEffect, useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import { parseISO, format } from "date-fns";
import {
    Upload, Loader2, Search, CheckCircle2, Clock, AlertTriangle,
    FileSpreadsheet, RefreshCw, Pencil, Calendar as CalendarIcon, X, Tag,
} from "lucide-react";

import { api } from "@/lib/api";
import { formatApiError } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const FREQ_OPTIONS = [
    { value: "all", label: "All frequencies" },
    { value: "monthly", label: "Monthly" },
    { value: "two_monthly_odd", label: "Two-Monthly (Odd)" },
    { value: "two_monthly_even", label: "Two-Monthly (Even)" },
    { value: "six_monthly_mar_sep", label: "Six-Monthly (Mar/Sep)" },
    { value: "six_monthly_jan_jul", label: "Six-Monthly (Jan/Jul)" },
    { value: "non_gst", label: "Non-GST" },
    { value: "unknown", label: "Unknown" },
];

const FREQ_LABELS = {
    monthly: "Monthly",
    two_monthly_odd: "Two-Monthly (Odd)",
    two_monthly_even: "Two-Monthly (Even)",
    six_monthly_mar_sep: "Six-Monthly (Mar/Sep)",
    six_monthly_jan_jul: "Six-Monthly (Jan/Jul)",
    non_gst: "Non-GST",
    unknown: "Unknown",
};

const BUCKET_STYLES = {
    overdue:   "bg-[#FBE8E8] text-[#7A1F1F] border-[#F1C8C8]",
    due_soon:  "bg-[#FFF4D6] text-[#6B4F00] border-[#F0E0A3]",
    upcoming:  "bg-[#E6F0FA] text-[#1E3A5F] border-[#C6DBF0]",
    filed:     "bg-[#E8F3EC] text-[#1F5235] border-[#C8E0D1]",
    none:      "bg-[#E8E8E4] text-[#5E636E] border-[#D4D4CF]",
};
const BUCKET_LABELS = {
    overdue: "Overdue",
    due_soon: "Due soon",
    upcoming: "Upcoming",
    filed: "Filed",
    none: "—",
};

const STATUS_COLORS = [
    { value: "slate",  dot: "#5E636E", pill: "bg-[#F0F0EB] text-[#0F1115] border-[#D4D4CF]" },
    { value: "blue",   dot: "#1E3A5F", pill: "bg-[#E6F0FA] text-[#1E3A5F] border-[#C6DBF0]" },
    { value: "green",  dot: "#1F5235", pill: "bg-[#E8F3EC] text-[#1F5235] border-[#C8E0D1]" },
    { value: "amber",  dot: "#6B4F00", pill: "bg-[#FFF4D6] text-[#6B4F00] border-[#F0E0A3]" },
    { value: "red",    dot: "#7A1F1F", pill: "bg-[#FBE8E8] text-[#7A1F1F] border-[#F1C8C8]" },
    { value: "purple", dot: "#4C1D95", pill: "bg-[#EDE5FA] text-[#4C1D95] border-[#D7C7F0]" },
];

function statusPill(color) {
    return STATUS_COLORS.find((c) => c.value === color)?.pill || STATUS_COLORS[0].pill;
}
function statusDot(color) {
    return STATUS_COLORS.find((c) => c.value === color)?.dot || STATUS_COLORS[0].dot;
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    } catch { return iso; }
}

function fmtDateTime(iso) {
    if (!iso) return "—";
    try {
        const d = new Date(iso);
        return d.toLocaleString(undefined, { year: "numeric", month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
    } catch { return iso; }
}

// Shared calendar input (same pattern as TaxPositionClient's DateInput)
function DateInput({ value, onChange, testid, placeholder = "Pick a date" }) {
    const d = value ? (() => {
        try { return parseISO(String(value).slice(0, 10)); } catch { return null; }
    })() : null;
    const [open, setOpen] = useState(false);
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button type="button" data-testid={testid}
                    className="h-9 w-full inline-flex items-center justify-between rounded-md border border-input bg-background px-3 text-[13px] text-[#0F1115] hover:bg-zinc-50">
                    <span className={d ? "" : "text-[#9CA3AF]"}>
                        {d ? format(d, "dd MMM yyyy") : placeholder}
                    </span>
                    <CalendarIcon className="h-3.5 w-3.5 text-[#5E636E]" />
                </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-0">
                <Calendar mode="single" selected={d}
                    onSelect={(dt) => { onChange(dt ? format(dt, "yyyy-MM-dd") : null); setOpen(false); }}
                    initialFocus />
            </PopoverContent>
        </Popover>
    );
}

export default function GstMonitor() {
    const [dash, setDash] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterFreq, setFilterFreq] = useState("all");
    const [filterBucket, setFilterBucket] = useState("all");
    const [q, setQ] = useState("");
    const [uploadResult, setUploadResult] = useState(null);
    const [uploading, setUploading] = useState(false);
    const fileRef = useRef(null);
    const [selected, setSelected] = useState(() => new Set());
    const [editing, setEditing] = useState(null);
    const [bulkMarkDialog, setBulkMarkDialog] = useState(false);
    const [bulkStatusDialog, setBulkStatusDialog] = useState(false);
    const [knownStatuses, setKnownStatuses] = useState([]);

    async function loadAll() {
        setLoading(true);
        try {
            const [d, r, s] = await Promise.all([
                api.get("/gst-monitor/dashboard"),
                api.get("/gst-monitor/clients"),
                api.get("/gst-monitor/statuses"),
            ]);
            setDash(d.data);
            setRows(r.data || []);
            setKnownStatuses(s.data || []);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => { loadAll(); }, []);

    const filtered = useMemo(() => {
        return rows.filter((r) => {
            if (filterFreq !== "all" && r.gst_filing_frequency !== filterFreq) return false;
            if (filterBucket !== "all" && r.bucket !== filterBucket) return false;
            if (q.trim()) {
                const s = q.trim().toLowerCase();
                if (!(r.customer_name || "").toLowerCase().includes(s)
                    && !(r.customer_id || "").toLowerCase().includes(s)) return false;
            }
            return true;
        });
    }, [rows, filterFreq, filterBucket, q]);

    const allVisibleSelected = filtered.length > 0 && filtered.every((r) => selected.has(r.customer_id));
    const someVisibleSelected = filtered.some((r) => selected.has(r.customer_id));

    function toggleRow(cid) {
        setSelected((prev) => {
            const n = new Set(prev);
            if (n.has(cid)) n.delete(cid); else n.add(cid);
            return n;
        });
    }
    function toggleAllVisible() {
        setSelected((prev) => {
            const n = new Set(prev);
            if (allVisibleSelected) {
                filtered.forEach((r) => n.delete(r.customer_id));
            } else {
                filtered.forEach((r) => n.add(r.customer_id));
            }
            return n;
        });
    }
    function clearSelection() { setSelected(new Set()); }

    async function onUpload(file) {
        if (!file) return;
        setUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post("/gst-monitor/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            setUploadResult(data);
            toast.success(`Imported ${data.inserted + data.updated} clients (${data.inserted} new, ${data.updated} updated).`);
            await loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setUploading(false);
            if (fileRef.current) fileRef.current.value = "";
        }
    }

    async function markFiledQuick(row) {
        if (!row.next_period_end) return;
        try {
            await api.post(`/gst-monitor/clients/${row.customer_id}/mark-filed`, {
                period_end: row.next_period_end,
            });
            toast.success(`Marked ${row.customer_name} as filed for ${fmtDate(row.next_period_end)}`);
            await loadAll();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    }

    const totalByFreq = dash?.frequency_counts || {};
    const buckets = dash?.bucket_counts || {};
    const selectedIds = Array.from(selected);

    return (
        <div className="space-y-5" data-testid="gst-monitor-page">
            <PageHeader
                title="GST Monitor"
                subtitle="Monitor NZ GST returns across your IRD client list."
                testId="gst-monitor-header"
            >
                <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={loadAll} data-testid="gst-refresh">
                        <RefreshCw className="h-4 w-4" /> Refresh
                    </Button>
                    <input
                        ref={fileRef}
                        type="file"
                        accept=".xlsx"
                        className="hidden"
                        onChange={(e) => onUpload(e.target.files?.[0])}
                        data-testid="gst-upload-input"
                    />
                    <Button
                        onClick={() => fileRef.current?.click()}
                        disabled={uploading}
                        className="co-btn-primary"
                        data-testid="gst-upload-btn"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
                        Upload IRD client list
                    </Button>
                </div>
            </PageHeader>

            {dash?.last_upload && (
                <div className="text-xs text-[#5E636E] flex items-center gap-2">
                    <FileSpreadsheet className="h-3.5 w-3.5" />
                    Last upload: <span className="font-medium text-[#0F1115]">{dash.last_upload.filename}</span>
                    {" · "}{fmtDateTime(dash.last_upload.uploaded_at)}
                    {" · "}by {dash.last_upload.uploaded_by_name}
                    {" · "}{dash.last_upload.inserted} new, {dash.last_upload.updated} updated
                </div>
            )}

            {/* Cadence tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
                <FreqTile label="Monthly" count={totalByFreq.monthly || 0} onClick={() => setFilterFreq("monthly")} active={filterFreq === "monthly"} testId="tile-monthly" />
                <FreqTile label="2-Monthly (Odd)" count={totalByFreq.two_monthly_odd || 0} onClick={() => setFilterFreq("two_monthly_odd")} active={filterFreq === "two_monthly_odd"} testId="tile-2m-odd" />
                <FreqTile label="2-Monthly (Even)" count={totalByFreq.two_monthly_even || 0} onClick={() => setFilterFreq("two_monthly_even")} active={filterFreq === "two_monthly_even"} testId="tile-2m-even" />
                <FreqTile label="6-Monthly (Mar/Sep)" count={totalByFreq.six_monthly_mar_sep || 0} onClick={() => setFilterFreq("six_monthly_mar_sep")} active={filterFreq === "six_monthly_mar_sep"} testId="tile-6m-mar-sep" />
                <FreqTile label="6-Monthly (Jan/Jul)" count={totalByFreq.six_monthly_jan_jul || 0} onClick={() => setFilterFreq("six_monthly_jan_jul")} active={filterFreq === "six_monthly_jan_jul"} testId="tile-6m-jan-jul" />
            </div>

            {/* Bucket tiles */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <BucketTile icon={<AlertTriangle className="h-4 w-4" />} label="Overdue" count={buckets.overdue || 0} tone="danger" onClick={() => setFilterBucket("overdue")} active={filterBucket === "overdue"} testId="bucket-overdue" />
                <BucketTile icon={<Clock className="h-4 w-4" />} label="Due soon (≤14d)" count={buckets.due_soon || 0} tone="warn" onClick={() => setFilterBucket("due_soon")} active={filterBucket === "due_soon"} testId="bucket-due-soon" />
                <BucketTile icon={<Clock className="h-4 w-4" />} label="Upcoming" count={buckets.upcoming || 0} tone="info" onClick={() => setFilterBucket("upcoming")} active={filterBucket === "upcoming"} testId="bucket-upcoming" />
                <BucketTile icon={<CheckCircle2 className="h-4 w-4" />} label="Filed" count={buckets.filed || 0} tone="success" onClick={() => setFilterBucket("filed")} active={filterBucket === "filed"} testId="bucket-filed" />
            </div>

            {/* Filters row */}
            <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2 flex-1 min-w-[220px]">
                    <Search className="h-4 w-4 text-[#5E636E]" />
                    <Input
                        placeholder="Search by name or IRD #…"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        className="h-9"
                        data-testid="gst-search"
                    />
                </div>
                <Select value={filterFreq} onValueChange={setFilterFreq}>
                    <SelectTrigger className="h-9 w-[220px]" data-testid="gst-filter-freq">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        {FREQ_OPTIONS.map((o) => (
                            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                        ))}
                    </SelectContent>
                </Select>
                <Select value={filterBucket} onValueChange={setFilterBucket}>
                    <SelectTrigger className="h-9 w-[200px]" data-testid="gst-filter-bucket">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="overdue">Overdue</SelectItem>
                        <SelectItem value="due_soon">Due soon</SelectItem>
                        <SelectItem value="upcoming">Upcoming</SelectItem>
                        <SelectItem value="filed">Filed</SelectItem>
                        <SelectItem value="none">No frequency</SelectItem>
                    </SelectContent>
                </Select>
                <div className="text-xs text-[#5E636E] ml-auto">
                    {filtered.length} of {rows.length} clients
                </div>
            </div>

            {/* Bulk toolbar */}
            {selected.size > 0 && (
                <div
                    className="rounded-md border border-[#0F1115] bg-[#0F1115] text-white px-3 py-2 flex items-center gap-2"
                    data-testid="bulk-toolbar"
                >
                    <span className="text-sm font-medium">{selected.size} selected</span>
                    <div className="ml-auto flex items-center gap-2">
                        <Button size="sm" variant="secondary" onClick={() => setBulkMarkDialog(true)} data-testid="bulk-mark-filed">
                            <CheckCircle2 className="h-3.5 w-3.5" /> Mark filed…
                        </Button>
                        <Button size="sm" variant="secondary" onClick={() => setBulkStatusDialog(true)} data-testid="bulk-set-status">
                            <Tag className="h-3.5 w-3.5" /> Set status…
                        </Button>
                        <Button size="sm" variant="ghost" className="text-white hover:bg-white/10" onClick={clearSelection}>
                            <X className="h-3.5 w-3.5" /> Clear
                        </Button>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="co-card overflow-hidden">
                {loading ? (
                    <div className="p-6 flex items-center justify-center gap-2 text-sm text-[#5E636E]">
                        <Loader2 className="h-4 w-4 animate-spin" /> Loading…
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-8 text-center">
                        <FileSpreadsheet className="h-10 w-10 text-[#5E636E] mx-auto mb-3" />
                        <div className="text-sm font-medium">No clients imported yet</div>
                        <div className="mt-1 text-xs text-[#5E636E] max-w-md mx-auto">
                            Upload the IRD "Client list report" Excel export (from myIR) to start monitoring GST returns across all registered customers.
                        </div>
                        <Button onClick={() => fileRef.current?.click()} className="mt-4 co-btn-primary" data-testid="gst-upload-empty-btn">
                            <Upload className="h-4 w-4" /> Upload client list
                        </Button>
                    </div>
                ) : filtered.length === 0 ? (
                    <div className="p-6 text-sm text-[#5E636E] text-center">No clients match these filters.</div>
                ) : (
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#FAFAF7] text-[11px] uppercase tracking-wide text-[#5E636E]">
                                <tr>
                                    <th className="p-3 w-[40px]">
                                        <Checkbox
                                            checked={allVisibleSelected}
                                            onCheckedChange={toggleAllVisible}
                                            data-testid="select-all"
                                            aria-label="Select all"
                                        />
                                    </th>
                                    <th className="text-left p-3">GST status</th>
                                    <th className="text-left p-3">Customer</th>
                                    <th className="text-left p-3">IRD #</th>
                                    <th className="text-left p-3">Subtype</th>
                                    <th className="text-left p-3">Frequency</th>
                                    <th className="text-left p-3">Next period end</th>
                                    <th className="text-left p-3">Next due</th>
                                    <th className="text-left p-3">Last filed</th>
                                    <th className="text-left p-3">Status note</th>
                                    <th className="text-right p-3">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r) => {
                                    const isSel = selected.has(r.customer_id);
                                    return (
                                        <tr
                                            key={r.id}
                                            className={`border-t border-[#E4E4E7] ${isSel ? "bg-[#F7F7F2]" : ""}`}
                                            data-testid={`gst-row-${r.customer_id}`}
                                        >
                                            <td className="p-3">
                                                <Checkbox
                                                    checked={isSel}
                                                    onCheckedChange={() => toggleRow(r.customer_id)}
                                                    aria-label={`Select ${r.customer_name}`}
                                                    data-testid={`select-${r.customer_id}`}
                                                />
                                            </td>
                                            <td className="p-3">
                                                <Badge variant="outline" className={`${BUCKET_STYLES[r.bucket] || BUCKET_STYLES.none} border`}>
                                                    {BUCKET_LABELS[r.bucket] || "—"}
                                                </Badge>
                                            </td>
                                            <td className="p-3 font-medium">{r.customer_name}</td>
                                            <td className="p-3 font-mono text-xs text-[#5E636E]">{r.customer_id}</td>
                                            <td className="p-3 text-[#5E636E]">{r.customer_subtype || "—"}</td>
                                            <td className="p-3 text-[#5E636E]">{FREQ_LABELS[r.gst_filing_frequency] || "—"}</td>
                                            <td className="p-3">
                                                {fmtDate(r.next_period_end)}
                                                {r.period_override && (
                                                    <div className="text-[10px] text-[#5E636E] italic">manual override</div>
                                                )}
                                            </td>
                                            <td className="p-3">
                                                {r.next_due_date ? (
                                                    <span className={r.bucket === "overdue" ? "text-[#7A1F1F] font-medium" : ""}>
                                                        {fmtDate(r.next_due_date)}
                                                    </span>
                                                ) : "—"}
                                            </td>
                                            <td className="p-3 text-[#5E636E] text-xs">
                                                {r.last_filed_period ? (
                                                    <>
                                                        {fmtDate(r.last_filed_period)}
                                                        <div className="text-[10px]">{r.last_filed_by}</div>
                                                    </>
                                                ) : "—"}
                                            </td>
                                            <td className="p-3">
                                                {r.status ? (
                                                    <Badge variant="outline" className={`${statusPill(r.status_color)} border`}>
                                                        {r.status}
                                                    </Badge>
                                                ) : (
                                                    <span className="text-[11px] text-[#9CA3AF]">—</span>
                                                )}
                                            </td>
                                            <td className="p-3 text-right whitespace-nowrap">
                                                {r.next_period_end && r.bucket !== "filed" && (
                                                    <Button size="sm" variant="outline" onClick={() => markFiledQuick(r)}
                                                        data-testid={`gst-mark-filed-${r.customer_id}`}>
                                                        <CheckCircle2 className="h-3.5 w-3.5" /> Mark filed
                                                    </Button>
                                                )}
                                                <Button size="sm" variant="ghost" onClick={() => setEditing(r)} className="ml-1"
                                                    data-testid={`gst-edit-${r.customer_id}`} title="Edit">
                                                    <Pencil className="h-3.5 w-3.5" />
                                                </Button>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {uploadResult && (
                <Dialog open onOpenChange={() => setUploadResult(null)}>
                    <DialogContent className="sm:max-w-[480px]" data-testid="gst-upload-result">
                        <DialogHeader>
                            <DialogTitle>Upload complete</DialogTitle>
                            <DialogDescription>
                                {uploadResult.upload?.filename} parsed. Existing clients were updated by Customer ID; new ones were inserted.
                            </DialogDescription>
                        </DialogHeader>
                        <dl className="grid grid-cols-2 gap-y-2 text-sm">
                            <dt className="text-[#5E636E]">Rows parsed</dt><dd>{uploadResult.total}</dd>
                            <dt className="text-[#5E636E]">New clients</dt><dd>{uploadResult.inserted}</dd>
                            <dt className="text-[#5E636E]">Updated</dt><dd>{uploadResult.updated}</dd>
                            <dt className="text-[#5E636E]">Skipped (no ID)</dt><dd>{uploadResult.skipped}</dd>
                        </dl>
                        <DialogFooter>
                            <Button onClick={() => setUploadResult(null)}>Close</Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>
            )}

            {editing && (
                <RowEditor
                    row={editing}
                    knownStatuses={knownStatuses}
                    onClose={() => setEditing(null)}
                    onSaved={async () => { setEditing(null); await loadAll(); }}
                />
            )}

            {bulkMarkDialog && (
                <BulkMarkDialog
                    ids={selectedIds}
                    onClose={() => setBulkMarkDialog(false)}
                    onDone={async () => { setBulkMarkDialog(false); clearSelection(); await loadAll(); }}
                />
            )}

            {bulkStatusDialog && (
                <BulkStatusDialog
                    ids={selectedIds}
                    knownStatuses={knownStatuses}
                    onClose={() => setBulkStatusDialog(false)}
                    onDone={async () => { setBulkStatusDialog(false); clearSelection(); await loadAll(); }}
                />
            )}
        </div>
    );
}

function RowEditor({ row, knownStatuses, onClose, onSaved }) {
    const [periodEnd, setPeriodEnd] = useState(row.next_period_end || "");
    const [lastFiled, setLastFiled] = useState(row.last_filed_period || "");
    const [status, setStatus] = useState(row.status || "");
    const [statusColor, setStatusColor] = useState(row.status_color || "slate");
    const [notes, setNotes] = useState(row.notes || "");
    const [saving, setSaving] = useState(false);

    async function save() {
        setSaving(true);
        try {
            await api.patch(`/gst-monitor/clients/${row.customer_id}`, {
                next_period_end: periodEnd || "",
                last_filed_period: lastFiled || "",
                status, status_color: statusColor, notes,
            });
            toast.success("Saved");
            await onSaved();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }

    async function resetPeriod() {
        if (!window.confirm("Reset the next period from the IRD filing frequency rules?")) return;
        try {
            await api.patch(`/gst-monitor/clients/${row.customer_id}`, { next_period_end: "" });
            toast.success("Period reset");
            await onSaved();
        } catch (e) { toast.error(formatApiError(e)); }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-[560px]" data-testid="row-editor">
                <DialogHeader>
                    <DialogTitle>Edit — {row.customer_name}</DialogTitle>
                    <DialogDescription>
                        {FREQ_LABELS[row.gst_filing_frequency] || "No GST frequency"} · IRD # {row.customer_id}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="co-label mb-1">Next period end</div>
                            <DateInput value={periodEnd} onChange={(v) => setPeriodEnd(v || "")} testid="editor-period-end" />
                            <div className="mt-1 text-[11px] text-[#5E636E]">Override only when IRD rules don't match.</div>
                        </div>
                        <div>
                            <div className="co-label mb-1">Last filed period</div>
                            <DateInput value={lastFiled} onChange={(v) => setLastFiled(v || "")} testid="editor-last-filed" />
                            <div className="mt-1 text-[11px] text-[#5E636E]">Set to the period you've just filed.</div>
                        </div>
                    </div>

                    <div>
                        <div className="co-label mb-1">Status note</div>
                        <Input
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            placeholder='e.g. "Prosaic connected", "Needs adjustments"'
                            data-testid="editor-status"
                            list="gst-status-list"
                        />
                        <datalist id="gst-status-list">
                            {knownStatuses.map((s) => (
                                <option key={s.label} value={s.label} />
                            ))}
                        </datalist>
                        <div className="mt-2 flex items-center gap-2">
                            <span className="text-[11px] text-[#5E636E]">Colour:</span>
                            {STATUS_COLORS.map((c) => (
                                <button
                                    key={c.value}
                                    type="button"
                                    onClick={() => setStatusColor(c.value)}
                                    className={`h-6 w-6 rounded-full border ${statusColor === c.value ? "ring-2 ring-offset-1 ring-[#0F1115]" : ""}`}
                                    style={{ backgroundColor: c.dot }}
                                    data-testid={`editor-color-${c.value}`}
                                    aria-label={c.value}
                                />
                            ))}
                        </div>
                        {knownStatuses.length > 0 && (
                            <div className="mt-2 flex flex-wrap gap-1">
                                {knownStatuses.slice(0, 6).map((s) => (
                                    <button
                                        key={s.label}
                                        type="button"
                                        onClick={() => { setStatus(s.label); if (s.color) setStatusColor(s.color); }}
                                        className={`text-[11px] px-2 py-1 rounded-full border ${statusPill(s.color)}`}
                                    >
                                        {s.label}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>

                    <div>
                        <div className="co-label mb-1">Notes</div>
                        <textarea
                            value={notes}
                            onChange={(e) => setNotes(e.target.value)}
                            rows={3}
                            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                            data-testid="editor-notes"
                        />
                    </div>
                </div>

                <DialogFooter className="flex-wrap gap-2">
                    <Button variant="ghost" onClick={resetPeriod} className="mr-auto" data-testid="editor-reset-period">
                        Reset period from rules
                    </Button>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={save} disabled={saving} className="co-btn-primary" data-testid="editor-save">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save changes
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkMarkDialog({ ids, onClose, onDone }) {
    const [useRowPeriod, setUseRowPeriod] = useState(true);
    const [period, setPeriod] = useState("");
    const [saving, setSaving] = useState(false);

    async function run() {
        setSaving(true);
        try {
            const payload = { customer_ids: ids };
            if (!useRowPeriod && period) payload.period_end = period;
            const { data } = await api.post("/gst-monitor/bulk/mark-filed", payload);
            toast.success(`${data.affected} client(s) marked filed`);
            await onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-[480px]" data-testid="bulk-mark-dialog">
                <DialogHeader>
                    <DialogTitle>Mark filed — {ids.length} client{ids.length === 1 ? "" : "s"}</DialogTitle>
                    <DialogDescription>
                        Choose how each client's filed period is set.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3 text-sm">
                    <label className="flex items-start gap-2">
                        <input type="radio" checked={useRowPeriod} onChange={() => setUseRowPeriod(true)} />
                        <div>
                            <div className="font-medium">Use each client's current period</div>
                            <div className="text-[12px] text-[#5E636E]">Each row's "Next period end" is marked as filed.</div>
                        </div>
                    </label>
                    <label className="flex items-start gap-2">
                        <input type="radio" checked={!useRowPeriod} onChange={() => setUseRowPeriod(false)} />
                        <div className="flex-1">
                            <div className="font-medium">Apply one specific period to all</div>
                            <div className="mt-1 w-[200px]">
                                <DateInput value={period} onChange={(v) => setPeriod(v || "")} testid="bulk-mark-period" />
                            </div>
                        </div>
                    </label>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button
                        onClick={run}
                        disabled={saving || (!useRowPeriod && !period)}
                        className="co-btn-primary"
                        data-testid="bulk-mark-confirm"
                    >
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Mark filed
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function BulkStatusDialog({ ids, knownStatuses, onClose, onDone }) {
    const [status, setStatus] = useState("");
    const [color, setColor] = useState("slate");
    const [saving, setSaving] = useState(false);

    async function run() {
        setSaving(true);
        try {
            const { data } = await api.post("/gst-monitor/bulk/set-status", {
                customer_ids: ids,
                status: status.trim(),
                status_color: color,
            });
            toast.success(status.trim()
                ? `${data.affected} client(s) tagged "${status.trim()}"`
                : `Status cleared for ${data.affected} client(s)`);
            await onDone();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSaving(false);
        }
    }

    return (
        <Dialog open onOpenChange={(v) => !v && onClose()}>
            <DialogContent className="sm:max-w-[480px]" data-testid="bulk-status-dialog">
                <DialogHeader>
                    <DialogTitle>Set status — {ids.length} client{ids.length === 1 ? "" : "s"}</DialogTitle>
                    <DialogDescription>
                        Create any label — e.g. "Prosaic connected", "Needs adjustments". Leave blank to clear.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-3">
                    <Input
                        value={status}
                        onChange={(e) => setStatus(e.target.value)}
                        placeholder="Status label (leave blank to clear)"
                        list="bulk-status-list"
                        data-testid="bulk-status-input"
                    />
                    <datalist id="bulk-status-list">
                        {knownStatuses.map((s) => <option key={s.label} value={s.label} />)}
                    </datalist>

                    {knownStatuses.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                            {knownStatuses.slice(0, 8).map((s) => (
                                <button
                                    key={s.label}
                                    type="button"
                                    onClick={() => { setStatus(s.label); if (s.color) setColor(s.color); }}
                                    className={`text-[11px] px-2 py-1 rounded-full border ${statusPill(s.color)}`}
                                >
                                    {s.label}
                                </button>
                            ))}
                        </div>
                    )}

                    <div className="flex items-center gap-2">
                        <span className="text-[11px] text-[#5E636E]">Colour:</span>
                        {STATUS_COLORS.map((c) => (
                            <button
                                key={c.value}
                                type="button"
                                onClick={() => setColor(c.value)}
                                className={`h-6 w-6 rounded-full border ${color === c.value ? "ring-2 ring-offset-1 ring-[#0F1115]" : ""}`}
                                style={{ backgroundColor: c.dot }}
                                aria-label={c.value}
                                data-testid={`bulk-color-${c.value}`}
                            />
                        ))}
                    </div>
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={onClose} disabled={saving}>Cancel</Button>
                    <Button onClick={run} disabled={saving} className="co-btn-primary" data-testid="bulk-status-confirm">
                        {saving && <Loader2 className="h-4 w-4 animate-spin" />} Apply
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

function FreqTile({ label, count, onClick, active, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`co-card co-card-pad text-left transition-colors ${active ? "ring-2 ring-[#0F1115]" : "hover:bg-[#FAFAF7]"}`}
        >
            <div className="text-[11px] uppercase tracking-wide text-[#5E636E]">{label}</div>
            <div className="mt-1 font-display text-2xl font-medium">{count}</div>
        </button>
    );
}

function BucketTile({ icon, label, count, tone, onClick, active, testId }) {
    const tones = {
        danger:  "text-[#7A1F1F]",
        warn:    "text-[#6B4F00]",
        info:    "text-[#1E3A5F]",
        success: "text-[#1F5235]",
    };
    return (
        <button
            type="button"
            onClick={onClick}
            data-testid={testId}
            className={`co-card co-card-pad text-left transition-colors ${active ? "ring-2 ring-[#0F1115]" : "hover:bg-[#FAFAF7]"}`}
        >
            <div className={`flex items-center gap-2 text-[11px] uppercase tracking-wide ${tones[tone] || ""}`}>
                {icon} {label}
            </div>
            <div className="mt-1 font-display text-2xl font-medium">{count}</div>
        </button>
    );
}
